import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import type { ClipCategory } from '../clip-category.types'

// ----------------------------------------------------------------------

const categoryFormSchema = z.object({
  label: z
    .string()
    .min(1, 'กรุณาป้อนชื่อหมวดหมู่')
    .max(60, 'ชื่อต้องไม่เกิน 60 ตัวอักษร'),
  isEnabled: z.boolean(),
})

type CategoryFormData = z.infer<typeof categoryFormSchema>

interface ClipCategoryFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: ClipCategory
  onSubmit: (values: CategoryFormData) => Promise<void>
}

export default function ClipCategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
}: ClipCategoryFormDialogProps) {
  const isEdit = !!category
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      label: '',
      isEnabled: true,
    },
  })

  useEffect(() => {
    if (open) {
      if (category) {
        form.reset({
          label: category.label,
          isEnabled: category.isEnabled,
        })
      } else {
        form.reset({ label: '', isEnabled: true })
      }
    }
  }, [open, category, form])

  const handleSubmit = async (values: CategoryFormData) => {
    setSubmitting(true)
    try {
      await onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-black">
            {isEdit ? `แก้ไขหมวดหมู่ — ${category.label}` : 'เพิ่มหมวดหมู่'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            id="clip-category-form"
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* label */}
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-black">
                    ชื่อหมวดหมู่ *
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="เช่น แข่งขัน"
                      className="rounded-lg border h-10"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* isEnabled */}
            <FormField
              control={form.control}
              name="isEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3 rounded-lg border p-3 space-y-0">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-medium text-black">
                    เปิดใช้งาน
                  </FormLabel>
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            ยกเลิก
          </Button>
          <Button type="submit" form="clip-category-form" disabled={submitting}>
            {submitting ? 'กำลังบันทึก...' : isEdit ? 'บันทึก' : 'เพิ่ม'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
